/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */


import RenderObject from "App/geometry/objects/object";
import AbstractGeometry from "App/geometry/objects_geometry/abstract_geometry";
import Factory from "App/geometry/objects/factory";
import GeometryFactory from "App/geometry/objects_geometry/factory";
import ModelDescription from "App/model/description";
import {RenderElement} from "App/model/elements/element";
import ShapeBatchNode from "App/render/webgl/nodes/shape_batch_node";
import ShapeBatch from "App/render/webgl/renderables/batches/shape_batch";
import {DataResource} from "App/storage/data_resource";

export default
class Shape extends RenderObject {
    public static TypeName: string = "SHAPE";

    public geometry: AbstractGeometry = null;

    constructor(aUID: number) {
        super(aUID);
    }

    public static create(aUID: number) {
        return new Shape(aUID);
    }

    public hasData(): boolean {
        return this.geometry.isInitialized();
    }

    public releaseGeometryData() {
        this.geometry.releaseGeometryData();
    }

    public getVerticesCount(): number {
        return this.geometry.getVerticesCount();
    }

    public getVerticesCountEstimation(): number {
        return this.geometry.getVerticesCountEstimation();
    }

    public getIndicesCount(): number {
        return this.geometry.getIndicesCount();
    }

    public getIndicesCountEstimation(): number {
        return this.geometry.getIndicesCountEstimation();
    }

    public getFacesCount(): number {
        return this.geometry.getFacesCount();
    }

    public getGeometry(): AbstractGeometry {
        return this.geometry;
    }

    public getPrimitiveSize(): number {
        return 3;
    }

    /**
     * function is called when a large block of binary data which contains data for this BinaryGeometry instance
     *
     * @param {unt8array} aResponse the larger block binary data received
     */
    public dataDownloaded(aResponse: Uint8Array, aDataResource: DataResource) {
        if (this.geometry) {
            this.geometry.loadData(aResponse);
        }

        // this is for the case when a model is transformed before its underlying geometry is downloaded
        if (this._onModelLoadTransformationFlag) {
            let modelTransformation = this.model.getOnModelLoadTransformationRef();
            if (modelTransformation) {
                this.transform(modelTransformation);
            }
            this._onModelLoadTransformationFlag = false;
        }

        // notify renderer (aka all render graphs) that shape is ready to be rendered because we do not know where
        // the shape is placed (just the geometry, no textures yet)
        this.model.getDocument().getRenderer().notifyMeshCreated(this);
        this.model.getDocument().extendBoundingBox(this);
    }

    public dataDownloadFailed() {
        this.model.getDocument().getRenderer().getLoadStatus().downloadFailed(null);
    }

    public dataDownloadAborted() {
        this.model.getDocument().getRenderer().getLoadStatus().downloadAborted(null);
    }

    public parse(aModelDescription: ModelDescription, aShapeJSON: any, aElement: RenderElement) {
        let geomTypePair = GeometryFactory.create(aShapeJSON, this);
        this.geometry = geomTypePair.geometry;

        this.geometry.parse(aShapeJSON[geomTypePair.type]);

        // then continue to appearance
        super.parse(aModelDescription, aShapeJSON, aElement);
    }

    public getLoadDataQuery(): any {
        let query = this.geometry.getLoadDataQuery();
        query.priority = this.getDownloadPriority();
        query.required = query.priority === 0;

        return query;
    }

    public removed() {
        super.removed();

        this.getGeometry().clear();
    }

    public getJSONRepresentation(): any {
        let res = super.getJSONRepresentation();

        res.binaryGeometry = this.geometry.getJSONRepresentation();

        return res;
    }

    public saveToDOM(): HTMLElement {
        let base64data = this.getGeometry().toBase64();

        let domElement = document.createElement("cwShapeGeometry");

        this.geometry.getProperties().src = "DOM" + this.geometry.getProperties().src;
        domElement.setAttribute("id", this.geometry.getProperties().src);
        domElement.setAttribute("base64data", base64data);

        return domElement;
    }
}

Factory.register(Shape);
ShapeBatchNode.registerGeometryObject(Shape.TypeName, ShapeBatch);
